import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ListPage } from "../../components/ListPage";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { FilterToggle } from "../../components/FilterToggle";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { useApi } from "../../helpers/useApi";
import { useProjectsQuery, useSitesQuery, useItemsQuery, useSuppliersQuery } from "../../helpers/queries";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { ServiceRequest, Staff, Paginated } from "../../api-models";

const TYPES = [
  { value: "VEHICLE", label: "Vehicle" },
  { value: "STAFF_TRANSFER", label: "Staff Transfer" },
  { value: "EQUIPMENT_TRANSFER", label: "Equipment Transfer" },
  { value: "TRANSPORT", label: "Transport" },
  { value: "RENTAL", label: "Rental" },
];

const STATUSES = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "REJECTED", "ASSIGNED", "IN_PROGRESS", "COMPLETED", "CLOSED", "CANCELLED"];

interface CreateFormValues {
  type: string;
  projectId?: number;
  siteId?: number;
  requestingDept?: string;
  fromLocation?: string;
  toLocation?: string;
  requiredAt?: dayjs.Dayjs;
  reason: string;
  remarks?: string;
  // VEHICLE
  vehicleTypeNeeded?: string;
  passengerOrLoad?: string;
  // STAFF_TRANSFER
  staffIds?: number[];
  fromProjectId?: number;
  fromSiteId?: number;
  toProjectId?: number;
  toSiteId?: number;
  effectiveDate?: dayjs.Dayjs;
  permanent?: boolean;
  expectedDurationNote?: string;
  accommodationNotes?: string;
  // EQUIPMENT_TRANSFER
  itemId?: number;
  qty?: number;
  assetSerial?: string;
  conditionAtRequest?: string;
  // TRANSPORT
  destinationSiteId?: number;
  origin?: string;
  transportedDescription?: string;
  // RENTAL
  vehicleTypeRequired?: string;
  estimatedCostAmount?: number;
  currency?: string;
  chargingPeriod?: string;
  supplierId?: number;
  quotationRef?: string;
}

export default function ServiceRequestsListPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const canWrite = hasAnyGrant(user, "ServiceRequests.Write");
  const { request, loading } = useApi();
  const { data: projects = [] } = useProjectsQuery();
  const { data: sites = [] } = useSitesQuery();
  const { data: items = [] } = useItemsQuery();
  const { data: suppliers = [] } = useSuppliersQuery();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<CreateFormValues>();
  const type = Form.useWatch("type", form);
  const projectId = Form.useWatch("projectId", form);
  const fromProjectId = Form.useWatch("fromProjectId", form);
  const toProjectId = Form.useWatch("toProjectId", form);

  // A site belongs to exactly one project, and the server refuses a pair that disagrees
  // (service_requests.go), so each site picker offers only the sites of the project picked
  // beside it - and the project pickers below clear their site when that project changes.
  function sitesIn(forProjectId?: number) {
    return forProjectId ? sites.filter((s) => s.projectId === forProjectId) : sites;
  }

  useEffect(() => {
    if (!open) return;
    request<Paginated<Staff>>({ url: "/staff", params: { pageSize: 1000 } })
      .then((res) => setStaff(res.data))
      .catch(() => setStaff([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function createRequest() {
    const v = await form.validateFields();
    const payload: Record<string, unknown> = {
      type: v.type,
      projectId: v.projectId,
      siteId: v.siteId,
      requestingDept: v.requestingDept,
      fromLocation: v.fromLocation,
      toLocation: v.toLocation,
      requiredAt: v.requiredAt?.toISOString(),
      reason: v.reason,
      remarks: v.remarks,
    };
    // A staff transfer's header project/site is the origin pair, filled in by the server
    // from the transfer detail, so the form neither shows nor sends it here.
    if (v.type === "STAFF_TRANSFER") {
      delete payload.projectId;
      delete payload.siteId;
    }
    if (v.type === "VEHICLE") {
      payload.vehicleDetail = { vehicleTypeNeeded: v.vehicleTypeNeeded, passengerOrLoad: v.passengerOrLoad };
    } else if (v.type === "STAFF_TRANSFER") {
      payload.staffTransferDetail = {
        staffIds: v.staffIds,
        fromProjectId: v.fromProjectId,
        fromSiteId: v.fromSiteId,
        toProjectId: v.toProjectId,
        toSiteId: v.toSiteId,
        effectiveDate: v.effectiveDate?.format("YYYY-MM-DD"),
        permanent: !!v.permanent,
        expectedDurationNote: v.expectedDurationNote,
        accommodationNotes: v.accommodationNotes,
      };
    } else if (v.type === "EQUIPMENT_TRANSFER") {
      payload.equipmentTransferDetail = { itemId: v.itemId, qty: v.qty, assetSerial: v.assetSerial, conditionAtRequest: v.conditionAtRequest };
    } else if (v.type === "TRANSPORT") {
      payload.transportDetail = { destinationSiteId: v.destinationSiteId, origin: v.origin, transportedDescription: v.transportedDescription };
    } else if (v.type === "RENTAL") {
      payload.rentalDetail = {
        vehicleTypeRequired: v.vehicleTypeRequired,
        estimatedCostAmount: v.estimatedCostAmount,
        currency: v.currency || "MVR",
        chargingPeriod: v.chargingPeriod,
        supplierId: v.supplierId,
        quotationRef: v.quotationRef,
      };
    }
    const created = await request<ServiceRequest>({ method: "POST", url: "/service-requests", data: payload });
    setOpen(false);
    navigate(`/service-requests/${created.id}`);
  }

  return (
    <>
      <ListPage<ServiceRequest>
        title="Service Requests"
        endpoint="/service-requests"
        searchPlaceholder="Search doc no, location or reason..."
        linkTo={(r) => `/service-requests/${r.id}`}
        actions={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              data-tour="service-requests-new"
              onClick={() => {
                form.resetFields();
                setOpen(true);
              }}
            >
              New service request
            </Button>
          )
        }
        filters={(controls) => (
          <Space>
            <Select
              allowClear
              placeholder="Type"
              style={{ width: 180 }}
              options={TYPES}
              value={controls.value("type")}
              onChange={(v) => controls.set("type", v)}
            />
            <Select
              allowClear
              placeholder="Status"
              style={{ width: 180 }}
              options={STATUSES.map((s) => ({ value: s, label: s.replaceAll("_", " ") }))}
              value={controls.value("status")}
              onChange={(v) => controls.set("status", v)}
            />
            {/* The dashboard's overdue-service-request tile lands here. */}
            <FilterToggle name="overdue" label="Overdue" controls={controls} />
          </Space>
        )}
        columns={[
          { title: "Doc No", dataIndex: "docNo", sorter: true },
          { title: "Type", dataIndex: "type", sorter: true, render: (v) => String(v).replaceAll("_", " ") },
          { title: "Project", render: (_, r) => <ProjectLink project={r.project} /> },
          { title: "Site", render: (_, r) => r.site?.name },
          { title: "Required by", dataIndex: "requiredAt", sorter: true, render: (v) => (v ? dayjs(v).format(DT_FORMATS.dateTime) : "—") },
          { title: "Status", dataIndex: "status", sorter: true, render: (v) => <DocumentStatusTag status={v} /> },
        ]}
        renderCard={(r) => (
          <div>
            <strong>{r.docNo}</strong>
            <div>
              {r.type.replaceAll("_", " ")} — <SiteLabel site={r.site} project={r.project} fallback={<ProjectLink project={r.project} fallback="" />} />
            </div>
          </div>
        )}
      />
      <Modal title="New service request" open={open} onCancel={() => setOpen(false)} onOk={createRequest} confirmLoading={loading} width={640} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES} />
          </Form.Item>
          {type !== "STAFF_TRANSFER" && (
            <Space style={{ width: "100%" }} size="middle">
              <Form.Item name="projectId" label="Project" style={{ width: 260 }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  allowClear
                  options={projects.map((p) => ({ value: p.id, label: p.name }))}
                  onChange={() => form.setFieldValue("siteId", undefined)}
                />
              </Form.Item>
              <Form.Item name="siteId" label="Site" style={{ width: 260 }}>
                <SiteSelect showSearch optionFilterProp="label" allowClear options={sitesIn(projectId).map((s) => ({ value: s.id, label: s.name }))} />
              </Form.Item>
            </Space>
          )}
          <Form.Item name="requestingDept" label="Requesting department">
            <Input />
          </Form.Item>
          <Space style={{ width: "100%" }} size="middle">
            <Form.Item name="fromLocation" label="From location" style={{ width: 260 }}>
              <Input />
            </Form.Item>
            <Form.Item name="toLocation" label="To location" style={{ width: 260 }}>
              <Input />
            </Form.Item>
          </Space>
          <Form.Item name="requiredAt" label="Required date/time">
            <DatePicker showTime style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type !== cur.type}>
            {({ getFieldValue }) => {
              const type = getFieldValue("type");
              if (type === "VEHICLE") {
                return (
                  <>
                    <Form.Item name="vehicleTypeNeeded" label="Vehicle type/characteristics needed" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="passengerOrLoad" label="Passenger/load details">
                      <Input />
                    </Form.Item>
                  </>
                );
              }
              if (type === "STAFF_TRANSFER") {
                return (
                  <>
                    <Form.Item name="staffIds" label="Staff member(s)" rules={[{ required: true }]}>
                      <Select mode="multiple" showSearch optionFilterProp="label" options={staff.map((s) => ({ value: s.id, label: `${s.staffCode} — ${s.name}` }))} />
                    </Form.Item>
                    <Space style={{ width: "100%" }} size="middle">
                      <Form.Item name="fromProjectId" label="From project" style={{ width: 260 }} rules={[{ required: true }]}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          options={projects.map((p) => ({ value: p.id, label: p.name }))}
                          onChange={() => form.setFieldValue("fromSiteId", undefined)}
                        />
                      </Form.Item>
                      <Form.Item name="fromSiteId" label="From site" style={{ width: 260 }} rules={[{ required: true }]}>
                        <SiteSelect showSearch optionFilterProp="label" options={sitesIn(fromProjectId).map((s) => ({ value: s.id, label: s.name }))} />
                      </Form.Item>
                    </Space>
                    <Space style={{ width: "100%" }} size="middle">
                      <Form.Item name="toProjectId" label="To project" style={{ width: 260 }} rules={[{ required: true }]}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          options={projects.map((p) => ({ value: p.id, label: p.name }))}
                          onChange={() => form.setFieldValue("toSiteId", undefined)}
                        />
                      </Form.Item>
                      <Form.Item name="toSiteId" label="To site" style={{ width: 260 }} rules={[{ required: true }]}>
                        <SiteSelect showSearch optionFilterProp="label" options={sitesIn(toProjectId).map((s) => ({ value: s.id, label: s.name }))} />
                      </Form.Item>
                    </Space>
                    <Form.Item name="effectiveDate" label="Effective transfer date" rules={[{ required: true }]}>
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="permanent" valuePropName="checked">
                      <Checkbox>Permanent transfer</Checkbox>
                    </Form.Item>
                    <Form.Item name="expectedDurationNote" label="Expected duration (if temporary)">
                      <Input />
                    </Form.Item>
                    <Form.Item name="accommodationNotes" label="Accommodation/transport notes">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </>
                );
              }
              if (type === "EQUIPMENT_TRANSFER") {
                return (
                  <>
                    <Space style={{ width: "100%" }} size="middle">
                      <Form.Item name="itemId" label="Item" style={{ width: 260 }} rules={[{ required: true }]}>
                        <Select showSearch optionFilterProp="label" options={items.map((i) => ({ value: i.id, label: `${i.code} — ${i.name}` }))} />
                      </Form.Item>
                      <Form.Item name="qty" label="Quantity" style={{ width: 260 }} rules={[{ required: true }]}>
                        <InputNumber min={0.001} style={{ width: "100%" }} />
                      </Form.Item>
                    </Space>
                    <Form.Item name="assetSerial" label="Asset/serial number">
                      <Input />
                    </Form.Item>
                    <Form.Item name="conditionAtRequest" label="Condition at request">
                      <Input />
                    </Form.Item>
                  </>
                );
              }
              if (type === "TRANSPORT") {
                return (
                  <>
                    <Form.Item name="destinationSiteId" label="Destination site" rules={[{ required: true }]}>
                      <SiteSelect showSearch optionFilterProp="label" options={sites.map((s) => ({ value: s.id, label: s.name }))} />
                    </Form.Item>
                    <Form.Item name="origin" label="Origin">
                      <Input />
                    </Form.Item>
                    <Form.Item name="transportedDescription" label="What is being transported">
                      <Input />
                    </Form.Item>
                  </>
                );
              }
              if (type === "RENTAL") {
                return (
                  <>
                    <Form.Item name="vehicleTypeRequired" label="Vehicle type required" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                    <Space style={{ width: "100%" }} size="middle">
                      <Form.Item name="estimatedCostAmount" label="Estimated cost (laari)" style={{ width: 260 }}>
                        <InputNumber min={0} style={{ width: "100%" }} />
                      </Form.Item>
                      <Form.Item name="currency" label="Currency" initialValue="MVR" style={{ width: 260 }}>
                        <Input />
                      </Form.Item>
                    </Space>
                    <Form.Item name="chargingPeriod" label="Charging period">
                      <Input placeholder="DAILY / WEEKLY / MONTHLY" />
                    </Form.Item>
                    <Form.Item name="supplierId" label="Supplier">
                      <Select allowClear showSearch optionFilterProp="label" options={suppliers.map((s) => ({ value: s.id, label: s.name }))} />
                    </Form.Item>
                    <Form.Item name="quotationRef" label="Quotation reference">
                      <Input />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
